import { useUnsavedActions } from '../../general/unsaved-changes-context';
import React, { lazy, Suspense, useEffect, useId, useRef, useState } from "react";
import { ExplainedButton as Button, DisabledActionHint, explainMenuItem } from '../explained-button';
import { SplitButton } from 'primereact/splitbutton';
import { Menu } from 'primereact/menu';
import { PrimeIcons } from "primereact/api";
import "../../assets/ContentEditor.css";
import "../../assets/admin-toolbar.css";
import { useManagedModuleEditor } from "../../providers/managed-module-management-provider";
import { ViewManagedModuleDialog } from "../dialogs/view-managed-module-dialog";
import { GeneralErrorDialog } from "../dialogs/error-dialog";
import { ModuleDto } from "../../general/dtos";
import { DeleteEntityConfirmDialog } from "../dialogs/delete-entity-confirm";
import { AuthoringChecklist } from './authoring-checklist';

const ModuleHistoryDialog = lazy(() => import('../admin-modules/module-history-dialog').then(module => ({ default: module.ModuleHistoryDialog })));
const ImportWordDialog = lazy(() => import('../admin-modules/import-word-dialog'));

export interface TopBarProps {
    onCancel: () => Promise<void>;
    onSave: (module: ModuleDto) => Promise<void>;
}

export function TopBar({ onCancel, onSave }: TopBarProps) {
  const { restoreRevision, restoreFragmentRevision, managedModule, fragments, addFragment, appendFragments, deleteFragment, selectedFragmentKey, saveManagedModule, saveModuleError, flushEditorChanges, clearRecovery, recoveryStatus } = useManagedModuleEditor();
  const { requestAction, flushPending } = useUnsavedActions();
  const fragmentMenu = useRef<Menu>(null);
  const fragmentMenuId = useId();
  const [fragmentMenuExpanded, setFragmentMenuExpanded] = useState(false);
  const [historyVisible, setHistoryVisible] = useState(false);
  const [wordImportVisible, setWordImportVisible] = useState(false);
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [testFromCurrent, setTestFromCurrent] = useState(false);
  const [previewStartFragmentKey, setPreviewStartFragmentKey] = useState<string | null>(null);
  const [showDeleteFragmentConfirm, setShowDeleteFragmentConfirm] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [errorTitle, setErrorTitle] = useState<string>('');
  const [errorDetails, setErrorDetails] = useState<string>('');

  useEffect(() => { setTestFromCurrent(false); }, [managedModule.module_key]);
  const testFragmentNumber = testFromCurrent ? Math.max(0, fragments.findIndex(fragment => fragment.key === selectedFragmentKey)) + 1 : 1;
  const startTest = () => {
    flushEditorChanges();
    setPreviewStartFragmentKey(testFromCurrent ? selectedFragmentKey : null);
    setDialogVisible(true);
  };

  useEffect(() => {
    if (saveModuleError) {
        setError(saveModuleError);
        setErrorTitle('Unable to save module');
        setErrorDetails('Your changes are still in the editor. Review the error and try saving again.');
    }
  }, [saveModuleError]);

  const handleAdd = () => {
    addFragment();
  };

  const handleDelete = () => {
    if (!selectedFragmentKey) return;
    deleteFragment(selectedFragmentKey);
  };

  const handleSave = async () => {
    const result: boolean = await saveManagedModule();
    if (result) {
        onSave(managedModule);
    }
  };

  const handleCancel = async () => {
    await clearRecovery();
    onCancel();
  };

  return (<>
    <div className="admin-toolbar flex justify-content-between align-items-center p-3 surface-card border-bottom-1 surface-border">
      <div className="admin-toolbar selection-controls">
        <Button label="Fragment" icon="pi pi-chevron-down" iconPos="right" className="p-button-text p-button-sm"
          aria-haspopup="menu" aria-controls={fragmentMenuId} aria-expanded={fragmentMenuExpanded}
          onClick={event => { flushEditorChanges(); fragmentMenu.current?.toggle(event); }} />
        <Menu ref={fragmentMenu} id={fragmentMenuId} popup pt={{ menu: { 'aria-label': 'Fragment actions' } }}
          onShow={() => setFragmentMenuExpanded(true)} onHide={() => setFragmentMenuExpanded(false)}
          model={[
            { label: 'Add', icon: PrimeIcons.PLUS, command: handleAdd },
            explainMenuItem({ label: 'Delete...', icon: PrimeIcons.TRASH, disabled: !selectedFragmentKey, command: () => setShowDeleteFragmentConfirm(true) }, 'Select a fragment to delete first.'),
          ]} />

        <Button label="Import Word..." icon="pi pi-file-word" className="p-button-text p-button-sm"
          title="Append fragments from a Word document" onClick={() => { flushEditorChanges(); setWordImportVisible(true); }} />

      </div>

      <div className="admin-toolbar selection-controls">
        {recoveryStatus && <span className="text-sm text-color-secondary" role="status" aria-label={recoveryStatus} title={recoveryStatus}>
            {recoveryStatus.startsWith('Recovery copy') ? <i className="pi pi-history" aria-hidden="true" /> : recoveryStatus}
        </span>}
        <Button label="History" icon="pi pi-history" className="p-button-text p-button-sm" disabled={!managedModule.module_key}
          disabledReason="Save the module once to start its revision history."
          title="Preview and restore saved module revisions" onClick={() => { flushPending(); flushEditorChanges(); setHistoryVisible(true); }} />
        <AuthoringChecklist />
        <DisabledActionHint label="Test module" reason={!fragments.length ? 'Add a fragment before testing this module.' : undefined}><SplitButton
            label={`Test (${testFragmentNumber})`}
            icon="pi pi-play"
            className={"p-button-text p-button-sm"}
            onClick={startTest}
            disabled={!fragments.length}
            tooltip={testFromCurrent ? 'Start from the currently selected fragment' : 'Start from the first fragment'}
            model={[
              { label: 'Start from the start', icon: !testFromCurrent ? 'pi pi-check' : undefined, command: () => setTestFromCurrent(false) },
              { label: 'Start from the current page', icon: testFromCurrent ? 'pi pi-check' : undefined, command: () => setTestFromCurrent(true) },
            ]}
            menuButtonProps={{ 'aria-label': 'Choose preview starting fragment' }}
            style={{ width: "auto" }}
        /></DisabledActionHint>
        <Button
          label="Save"
          icon={PrimeIcons.SAVE}
          className="p-button-text p-button-sm"
          onClick={handleSave}
          aria-label="Save module"
        />
        <Button
          label="Cancel"
          icon={PrimeIcons.MINUS}
          className="p-button-text p-button-sm"
          onClick={handleCancel}
          aria-label="Cancel editing"
        />
      </div>
    </div>

    {historyVisible && <Suspense fallback={<span role="status">Loading history...</span>}><ModuleHistoryDialog key={managedModule.module_key} module={managedModule} onClose={() => setHistoryVisible(false)}
      onRestoreFragment={(snapshot, key) => { setHistoryVisible(false); restoreFragmentRevision(snapshot, key); }}
      onRestore={snapshot => { setHistoryVisible(false); requestAction(() => restoreRevision(snapshot)); }} /></Suspense>}
    {wordImportVisible && <Suspense fallback={<p role="status">Loading Word import…</p>}>
      <ImportWordDialog mode="append" folderId={managedModule.folder_id ?? null} targetName={managedModule.name}
        onHide={() => setWordImportVisible(false)} onPrepared={module => {
          appendFragments(module.fragments ?? []);
          setWordImportVisible(false);
        }} />
    </Suspense>}

    <ViewManagedModuleDialog 
        contentLoading={false}
        fullModule={managedModule}
        onClose={() => setDialogVisible(false)}
        visible={dialogVisible}
        isTestMode={true}
        initialFragmentKey={previewStartFragmentKey}
        allowFragmentSelection
    />

    <GeneralErrorDialog
        visible={error.length > 0}
        title={errorTitle}
        message={error}
        details={errorDetails}
        onClose={() => setError('')}
    />

    <DeleteEntityConfirmDialog 
        visible={showDeleteFragmentConfirm}
        onCancel={() => setShowDeleteFragmentConfirm(false)}
        onConfirm={async () => {
            handleDelete();
            setShowDeleteFragmentConfirm(false);
        }}
        entityType='Module fragment'
        entityName={fragments?.find(f => f.key === selectedFragmentKey)?.title ?? ''}
    />
  </>);
}
